export type SlideOwnerStats = {
  pageCount: number;
  layoutBytes: number;
};

const STORAGE_PREFIX = 'cp:program-presentation-list-stats:v1:';

const memoryByWorkspace = new Map<string, Record<string, SlideOwnerStats>>();

function draftStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function loadRecord(workspaceId: string): Record<string, SlideOwnerStats> {
  const sid = workspaceId.trim();
  if (!sid) return {};
  const mem = memoryByWorkspace.get(sid);
  if (mem) return mem;
  const storage = draftStorage();
  if (!storage) {
    memoryByWorkspace.set(sid, {});
    return {};
  }
  try {
    const raw = storage.getItem(STORAGE_PREFIX + sid);
    const parsed = raw ? (JSON.parse(raw) as Record<string, SlideOwnerStats>) : {};
    const normalized = parsed && typeof parsed === 'object' ? parsed : {};
    memoryByWorkspace.set(sid, normalized);
    return normalized;
  } catch {
    memoryByWorkspace.set(sid, {});
    return {};
  }
}

function saveRecord(workspaceId: string, record: Record<string, SlideOwnerStats>): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  memoryByWorkspace.set(sid, record);
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_PREFIX + sid, JSON.stringify(record));
  } catch {
    /* quota 등은 무시 */
  }
}

export function invalidateProgramPresentationListStatsCache(workspaceId: string): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  memoryByWorkspace.delete(sid);
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_PREFIX + sid);
  } catch {
    /* ignore */
  }
}

export function readProgramPresentationListStatsCache(
  workspaceId: string,
  ownerKeys: string[]
): Map<string, SlideOwnerStats> {
  const sid = workspaceId.trim();
  const out = new Map<string, SlideOwnerStats>();
  if (!sid || !ownerKeys.length) return out;
  const record = loadRecord(sid);
  ownerKeys.forEach((key) => {
    const hit = record[key];
    if (hit && Number.isFinite(hit.pageCount) && Number.isFinite(hit.layoutBytes)) {
      out.set(key, { pageCount: hit.pageCount, layoutBytes: hit.layoutBytes });
    }
  });
  return out;
}

/** 워크스페이스 전체 stats 레코드(동기) */
export function loadProgramPresentationListStatsRecord(
  workspaceId: string
): Record<string, SlideOwnerStats> {
  return loadRecord(workspaceId.trim());
}

export function patchProgramPresentationListStatsCache(
  workspaceId: string,
  patch: Map<string, SlideOwnerStats> | Record<string, SlideOwnerStats>
): void {
  writeProgramPresentationListStatsCache(workspaceId, patch);
}

/** 특정 owner stats만 제거 — 슬라이드 저장 후 partial refetch 유도 */
export function removeProgramPresentationListStatsCacheKeys(
  workspaceId: string,
  ownerKeys: string[]
): void {
  const sid = workspaceId.trim();
  if (!sid || !ownerKeys.length) return;
  const prev = loadRecord(sid);
  const next = { ...prev };
  let changed = false;
  ownerKeys.forEach((key) => {
    if (key in next) {
      delete next[key];
      changed = true;
    }
  });
  if (changed) saveRecord(sid, next);
}

export function writeProgramPresentationListStatsCache(
  workspaceId: string,
  patch: Map<string, SlideOwnerStats> | Record<string, SlideOwnerStats>
): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  const prev = loadRecord(sid);
  const next = { ...prev };
  if (patch instanceof Map) {
    patch.forEach((stats, key) => {
      next[key] = stats;
    });
  } else {
    Object.entries(patch).forEach(([key, stats]) => {
      next[key] = stats;
    });
  }
  saveRecord(sid, next);
}
